import { Component, computed, inject, input, output } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { SiteWorkflowDirective } from '@features/feature-workflow/directives/site-workflow.directive';

import { injectSiteProtocolVersions } from '@models/sites/api/sites.api';

import { ButtonComponent } from '@shared/components/button/button.component';
import { ButtonItemOption } from '@shared/components/button-list/button-list.component';
import { ErrorsTooltipComponent } from '@shared/components/errors-tooltip/errors-tooltip.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  createSiteBudgetVersionMutation,
  listSiteBudgetVersionsQuery,
  PermissionType,
  WorkflowStep,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { Maybe } from '@shared/utils/utils';

import { SiteBudgetAmendmentModalComponent } from './components/site-budget-amendment-modal/site-budget-amendment-modal.component';

@Component({
  hostDirectives: [SiteWorkflowDirective],
  imports: [ButtonComponent, TooltipDirective, ErrorsTooltipComponent],
  selector: 'aux-site-amendment-button',
  standalone: true,
  template: `
    @let reasonsToDisable = siteWorkflow.updateSiteCostTooltips();

    <aux-button
      icon="Plus"
      label="Site Amendment"
      variant="secondary"
      [disabled]="isDisabled()"
      [auxTooltip]="reasonsToDisable.length === 1 ? reasonsToDisable[0] : ''"
      [template]="reasonsToDisable.length > 1 ? tooltipTemplate : undefined"
      (click)="createSiteBudgetAmendment()"
    />

    <ng-template #tooltipTemplate>
      <aux-errors-tooltip [errors]="reasonsToDisable" />
    </ng-template>
  `,
})
export class SiteAmendmentButtonComponent {
  siteWorkflow = inject(SiteWorkflowDirective);

  private authService = inject(AuthService);
  private overlayService = inject(OverlayService);
  private workflowQuery = inject(WorkflowQuery);

  protocolVersions = injectSiteProtocolVersions();

  disabledTooltip = computed(() => {
    if (!this.hasUpdateSiteCostPermission()) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }
  });

  disabled = input(false);

  hasUpdateSiteCostPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPDATE_SITE_COSTS],
    sysAdminsOnly: false,
  });
  isPatientsFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER
  );

  isDisabled = computed(() => {
    return (
      !this.hasUpdateSiteCostPermission() ||
      this.isPatientsFinalized() ||
      this.protocolVersions.isFetching() ||
      !this.protocolVersions.data()?.length ||
      this.disabled()
    );
  });

  selectedProtocolVersion = input.required<null | string>();
  selectedSiteId = input.required<Maybe<string>>();

  amendmentCreated = output<listSiteBudgetVersionsQuery>();

  async createSiteBudgetAmendment() {
    const protocolVersionList: ButtonItemOption[] =
      this.protocolVersions.data()?.map((version) => ({
        label: version.name,
        value: version.id,
      })) || [];

    const modalEvent = this.overlayService.openPopup<
      {
        protocolVersion: null | string;
        protocolVersionList: ButtonItemOption[];
        siteId: string;
      },
      { siteBudget?: createSiteBudgetVersionMutation },
      SiteBudgetAmendmentModalComponent
    >({
      content: SiteBudgetAmendmentModalComponent,
      data: {
        protocolVersion: this.selectedProtocolVersion(),
        protocolVersionList: protocolVersionList,
        siteId: this.selectedSiteId() ?? '',
      },
      settings: {
        header: 'New Site Budget Amendment',
        primaryButton: {
          action: (instance) => instance?.createAmendment(),
          label: 'Create',
        },
      },
    }).afterClosed$;

    const resp = await firstValueFrom(modalEvent);

    if (resp?.data?.siteBudget?.id) {
      this.overlayService.showFieldTooltip('siteBudgetVersion', 'Amendment Created');
      const siteBudget = resp.data.siteBudget as listSiteBudgetVersionsQuery;

      this.amendmentCreated.emit(siteBudget);
    }
  }
}
